/**
 * Vue Scripts
 *
 * @link https://vue-final-modal.org/api/components/vue-final-modal/
 * @link https://pinia.vuejs.org/introduction.html
 */
import {createApp} from "vue"
import {createPinia} from "pinia"
import {createVfm} from 'vue-final-modal'

//region Component imports
/**
 * Pages
 */
import Home from "@pages/Home/Home.vue"
import SearchBox from "@pages/Search/SearchBox/SearchBox.vue"
import SearchFilter from "@pages/Search/Filter/Filter.vue"
import SearchResults from "@pages/Search/Results/Results.vue"
import SearchPagination from "@pages/Search/Pagination/Pagination.vue"
import TrialListingLocation from "@pages/Trial/TrialMap/Listing/Listing.vue"
import TrialListingMap from "@pages/Trial/TrialMap/Map/Map.vue"
import ProgramListingLocation from "@pages/Program/ProgramMap/Listing/Listing.vue"
import ProgramListingMap from "@pages/Program/ProgramMap/Map/Map.vue"
import ProgramCheckboxFilter from "@pages/Program/ProgramMap/Filters/CheckboxFilter/CheckboxFilter.vue";
import GlossarySearch from "@pages/Glossary/Search/Search.vue"
import GlossaryFilter from "@pages/Glossary/Filter/Filter.vue"
import GlossaryResults from "@pages/Glossary/Results/Results.vue"

/**
 * Components
 */
import LanguageDropdown from "@global/LanguageDropdown/LanguageDropdown.vue"
import NavigationSearch from "@global/NavigationSearch/NavigationSearch.vue"
import AddToDesktop from "@global/AddToDesktop/AddToDesktop.vue"

/**
 * Modals
 */
import ExternalLinkModal from "@global/ExternalLinkModal/ExternalLinkModal.vue"
import OutOfCountryModal from "@global/OutOfCountryModal/OutOfCountryModal.vue"
import HcpModal from "@pages/HCPModal/HcpModal.vue"

/**
 * Modules
 */
import TrialFilterBar from "@modules/TrialListing/FilterBar/FilterBar.vue"
import TrialResultsCount from "@modules/TrialListing/ResultsCount/ResultsCount.vue"
import TrialListing from "@modules/TrialListing/Listing/Listing.vue"
import TrialPagination from "@modules/TrialListing/Pagination/Pagination.vue"
import MatchMe from "@modules/Antidote/Antidote.vue"

import TrialSearch from "@modules/TrialSearch/Search.vue"

import StudyKikForm from "@global/StudyKikForm/StudyKikForm.vue";

//endregion

/**
 * Defines
 */
const pinia = createPinia()

//region Global
// region Navigation Dropdown
const navigationSearchElm = document.getElementById('vue-navigation-container')
if (navigationSearchElm) {
    const navigationSearchApp = createApp(NavigationSearch)
    navigationSearchApp.use(pinia)
    navigationSearchApp.mount(navigationSearchElm)
}
//endregion

//region Language Dropdown
const languageDropdownElm = document.getElementById('vue-lang-dropdown-container')
/**
 * Language Dropdown
 */
if (languageDropdownElm) {
    const languageDropdownApp = createApp(LanguageDropdown)
    languageDropdownApp.use(pinia)
    languageDropdownApp.mount(languageDropdownElm)
}
//endregion

//region Add To Desktop
const addToDesktopElm = document.getElementById('vue-nav-add-to-desktop')
const addToMobileElm = document.getElementById('vue-mobile-util-add-to-desktop')
/**
 * Add to Desktop for Desktop View
 */
if (addToDesktopElm ) {
    const addToDesktopApp = createApp(AddToDesktop)
    addToDesktopApp.use(pinia)
    addToDesktopApp.mount(addToDesktopElm)
}

/**
 * Add to Desktop for Mobile View
 */
if (addToMobileElm) {
    const addToDesktopMobileApp = createApp(AddToDesktop)
    addToDesktopMobileApp.use(pinia)
    addToDesktopMobileApp.mount(addToMobileElm)
}
//endregion

//region External Modal
const externalModalElm = document.getElementById('vue-external-modal')
if (externalModalElm) {
    const externalModalApp = createApp(ExternalLinkModal)
    externalModalApp.use(createVfm())
    externalModalApp.mount(externalModalElm)
}
//endregion

//region Country Popup
const outOfCountryElm = document.getElementById('vue-country-popup-container')
if (outOfCountryElm) {
    const outOfCountryApp = createApp(OutOfCountryModal)
    outOfCountryApp.use(createVfm())
    outOfCountryApp.mount(outOfCountryElm)
}
//endregion

const studyKikFormElm = document.getElementById('vue-studykik')
if (studyKikFormElm) {
    const studyKikFormApp = createApp(StudyKikForm)
    studyKikFormApp.use(pinia)
    studyKikFormApp.mount(studyKikFormElm)
}

//endregion

//region Pages
// region Homepage Predictive Search
const homeFormElm = document.getElementById("vue-home-search-container")
/**
 * Homepage Predictive Search Box
 */
if (homeFormElm) {
    const homeApp = createApp(Home)
    homeApp.use(pinia)
    homeApp.mount(homeFormElm)
}
// endregion

//region Search Page
const searchBoxElm = document.getElementById('search-box')
const searchFilterElm = document.getElementById('search-filter')
const searchResultsElm = document.getElementById('search-results')
const searchPaginationElm = document.getElementById('search-pagination')

if (searchBoxElm) {
    const searchBoxApp = createApp(SearchBox)
    searchBoxApp.use(pinia)
    searchBoxApp.mount(searchBoxElm)
}

if (searchFilterElm) {
    const searchFilterApp = createApp(SearchFilter)
    searchFilterApp.use(pinia)
    searchFilterApp.mount(searchFilterElm)
}

if (searchResultsElm) {
    const searchResultsApp = createApp(SearchResults)
    searchResultsApp.use(pinia)
    searchResultsApp.mount(searchResultsElm)
}

if (searchPaginationElm) {
    const searchPaginationApp = createApp(SearchPagination)
    searchPaginationApp.use(pinia)
    searchPaginationApp.mount(searchPaginationElm)
}

//endregion

//region HCP Popup
const hcpModalElm = document.getElementById('vue-hcp-popup-container')
if (hcpModalElm) {
    const hcpModalApp = createApp(HcpModal)
    hcpModalApp.use(createVfm())
    hcpModalApp.mount(hcpModalElm)
}
//endregion

//region Trial Map
const trialMapListingElm = document.getElementById("vue-trial-listing")
const trialMapElm = document.getElementById("vue-trial-map")

if (trialMapListingElm) {
    const trialMapListingApp = createApp(TrialListingLocation)
    trialMapListingApp.use(pinia)
    trialMapListingApp.mount(trialMapListingElm)
}

if (trialMapElm) {
    const trialMapApp = createApp(TrialListingMap)
    trialMapApp.use(pinia)
    trialMapApp.mount(trialMapElm)
}
//endregion

//region Program Map
const programMapListingElm = document.getElementById("vue-program-listing")
const programMapElm = document.getElementById("vue-program-map")
const programFiltersElm = document.getElementById("vue-program-filters")

if (programMapListingElm) {
    const programMapListingApp = createApp(ProgramListingLocation)
    programMapListingApp.use(pinia)
    programMapListingApp.mount(programMapListingElm)
}

if (programMapElm) {
    const programMapApp = createApp(ProgramListingMap)
    programMapApp.use(pinia)
    programMapApp.mount(programMapElm)
}

if (programFiltersElm) {
    const programFiltersApp = createApp(ProgramCheckboxFilter)
    programFiltersApp.use(pinia)
    programFiltersApp.mount(programFiltersElm)
}
//endregion

//region Glossary
const glossarySearchElm = document.getElementById('vue-glossary-search')
const glossaryFilterElm = document.getElementById('vue-glossary-filter')
const glossaryResultsElm = document.getElementById('vue-glossary-results')

if (glossarySearchElm) {
    const glossarySearchApp = createApp(GlossarySearch)
    glossarySearchApp.use(pinia)
    glossarySearchApp.mount(glossarySearchElm)
}

if (glossaryFilterElm) {
    const glossaryFilterApp = createApp(GlossaryFilter)
    glossaryFilterApp.use(pinia)
    glossaryFilterApp.mount(glossaryFilterElm)
}

if (glossaryResultsElm) {
    const glossaryResultsApp = createApp(GlossaryResults)
    glossaryResultsApp.use(pinia)
    glossaryResultsApp.mount(glossaryResultsElm)
}

//endregion
//endregion

//region Modules
//region Match Me Module
const matchMeModuleElm = document.getElementById('vue-oncology-search')

if (matchMeModuleElm) {
    const matchMeApp = createApp(MatchMe)
    matchMeApp.use(pinia)
    matchMeApp.mount(matchMeModuleElm)
}
//endregion

//region Trial Listing Module
const trialFilterElm = document.getElementById('vue-trial-listing-filter')
const trialResultsCountElm = document.getElementById('vue-trial-listing-results')
const trialListingElm = document.getElementById('vue-trial-listing-trials')
const trialPaginationElm = document.getElementById('vue-trial-listing-pagination')

if (trialFilterElm) {
    const trialFilterApp = createApp(TrialFilterBar)
    trialFilterApp.use(pinia)
    trialFilterApp.mount(trialFilterElm)
}

if (trialResultsCountElm) {
    const trialResultsCountApp = createApp(TrialResultsCount)
    trialResultsCountApp.use(pinia)
    trialResultsCountApp.mount(trialResultsCountElm)
}

if (trialListingElm) {
    const trialListingApp = createApp(TrialListing)
    trialListingApp.use(pinia)
    trialListingApp.mount(trialListingElm)
}

if (trialPaginationElm) {
    const trialPaginationApp = createApp(TrialPagination)
    trialPaginationApp.use(pinia)
    trialPaginationApp.mount(trialPaginationElm)
}
//endregion

//region Trial Search
const trialSearchElm = document.getElementById('vue-trial-search')

if (trialSearchElm) {
    const trialSearchApp = createApp(TrialSearch)
    trialSearchApp.use(pinia)
    trialSearchApp.mount(trialSearchElm)
}
//endregion

//endregion
